import Accordion from "@components/Accordion/Accordion";
import CallAdditionalDocumentsPreview from "@features/vessel-call/vessel-call-status/VesselCallDetails/CallAdditionalDocumentsPreview";
import ImoFALPreview from "@features/vessel-call/vessel-call-status/VesselCallDetails/ImoFALPreview";
import VesselCallCertificates from "@features/vessel-call/vessel-call-status/VesselCallDetails/VesselCallCertificates.tsx";
import VesselInformation from "@features/vessel-call/vessel-call-status/VesselCallDetails/VesselInformation.tsx";
import VesselCallPreArrivalNotification from "@features/vessel-call/vessel-call-status/VesselCallDetails/VesselPreArrivalNotification.tsx";
import type {
  VesselCallNotificationDTO,
  VesselInformationDTO,
} from "@features/vessel-call/vessel-call-status/VesselCallStatus.types.tsx";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import { type Company } from "@services/vessel-call/types";
import type { Vessel } from "@services/vessel/types";
import type { VesselCallResponse } from "@services/vesselCall/types";
import type { VesselRegisterDetailsFixed } from "@services/vesselCall/types.tsx";
import { checkPermission } from "@utils/user-permissions";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { buildVesselCallNotificationDetails, buildVesselInformationDetails } from "./utils";

const TEST_ID_BASE = "vessel-status-page";
const LANG_BASE = "vessel-call";

const VesselCallDetails = () => {
  const { t } = useTranslation();
  const authStore = useAuthStore();
  const { vesselRegisterDetails, vesselCallDetails, vesselRegisterDetailsFixed } =
    useOutletContext<{
      vesselRegisterDetails: Vessel | null;
      vesselCallDetails: VesselCallResponse | null;
      vesselRegisterDetailsFixed?: VesselRegisterDetailsFixed | null;
    }>();
  const { userAccessPermissions } = useAuthStore();
  const isMarineDepartment = checkPermission(userAccessPermissions, "hasChangeVesselStatusAccess");

  const { options: companies } = useFormattedReference<Company>(
    `${REFERENCE_DATA_TYPES.COMPANIES}?isShippingAgency=true`,
    "bff-vessel-call",
  );
  const vesselInformationDetails: VesselInformationDTO = buildVesselInformationDetails(
    vesselRegisterDetailsFixed,
  );
  const vesselCallNotificationDetails: VesselCallNotificationDTO =
    buildVesselCallNotificationDetails(vesselCallDetails, companies);
  const canViewVesselDetails =
    authStore.user?.companyId === vesselRegisterDetails?.companyId ||
    authStore.user?.companyId === vesselCallDetails?.companyId ||
    isMarineDepartment;
  return (
    <>
      {canViewVesselDetails && (
        <>
          <div className="mb-8 no-border">
            <Accordion
              defaultExpanded
              testId={`${TEST_ID_BASE}-accordion-vessel-details`}
              summary={t(`${LANG_BASE}.vessel-information`)}
            >
              <VesselInformation vesselDetails={vesselInformationDetails} />
            </Accordion>
          </div>
          <div className="mb-8 no-border">
            <Accordion
              testId={`${TEST_ID_BASE}-accordion-vessel-details`}
              summary={t(`${LANG_BASE}.ship-certificates`)}
            >
              <VesselCallCertificates
                certificates={vesselRegisterDetails?.latestApprovedCertificates || []}
              />
            </Accordion>
          </div>
        </>
      )}
      <div className="mb-8 no-border">
        <Accordion
          defaultExpanded={!canViewVesselDetails}
          testId={`${TEST_ID_BASE}-accordion-vessel-details`}
          summary={t(`${LANG_BASE}.vessel-pre-arrival-notification`)}
        >
          <VesselCallPreArrivalNotification vesselDetails={vesselCallNotificationDetails} />
        </Accordion>
      </div>
      <div className="mb-8 no-border">
        <Accordion
          testId={`${TEST_ID_BASE}-accordion-vessel-details`}
          summary={t(`${LANG_BASE}.IMOFAL`)}
        >
          <ImoFALPreview
            vesselCallDetails={vesselCallDetails}
            vesselRegisterDetails={vesselRegisterDetails}
          />
        </Accordion>
      </div>
      <div className="mb-8 no-border">
        <Accordion
          testId={`${TEST_ID_BASE}-accordion-vessel-details`}
          summary={t(`${LANG_BASE}.vessel-additional-documents`)}
        >
          <CallAdditionalDocumentsPreview
            additionalRemarks={vesselCallDetails?.additionalRemarks}
            additionalDocuments={vesselCallDetails?.additionalDocuments}
          />
        </Accordion>
      </div>
    </>
  );
};

export default VesselCallDetails;
